import {
  Stack,
  Box,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import BoldSubtitle from "src/components/Displays/BoldSubtitle";
import TextSubtitle from "src/components/Displays/TextSubtitle";
import StartCalculatorModal from "src/components/Modal/StartCalculatorModal";
import PrimaryButton from "src/components/Shared/PrimaryButton";
import { ROUTES } from "src/consts/routes";
import { useToggle } from "src/hooks/useToggle";
import {
  CheckCircle,
  PlayCircleBlack,
  StromPreiseSMA,
  TestemonialPortrait,
} from "src/assets";
import CertificateSection from "src/components/Common/CertificateSection";
import Text from "src/components/Displays/Text";
import CalculatedSavingsSummary from "src/pages/Tarif/components/CalculatedSavingsSummary";
import { SESSION_KEYS, BONUS } from "src/consts/session";
import { useSessionStorage } from "src/hooks/useSessionStorage";
import { Tariff } from "src/models/clientsModel";
import { useMemo, useState, useEffect, useRef } from "react";
import { dateFormat } from "src/helpers/dateFormat";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PreCalcSkeletonSummary from "src/pages/Tarif/components/PreCalcSkeletonSummary";

const Content = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isModalOpen, toggleModal] = useToggle(false);

  const [expandedPanel, setExpandedPanel] = useState<string | false>(false);

  const handleAccordionChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedPanel(isExpanded ? panel : false);
    };

  const [searchParams] = useSearchParams();
  const tid = searchParams.get("tid") ?? "";
  const spid = searchParams.get("spid") ?? "";

  const isStageOrProdEnv =
    process.env.REACT_APP_BUILD_ENV === "STAGE" ||
    process.env.REACT_APP_BUILD_ENV === "PROD";

  const showOverlay =
    (tid.length === 0 || spid.length === 0) && isStageOrProdEnv;

  const handleStartCalculator = () => {
    if (showOverlay) {
      toggleModal();
      return;
    }
    navigate(ROUTES.PV_SETUP);
  };

  const handleModalSubmit = () => {
    const paPoSignUpUrl = process.env.REACT_APP_PAPO_SIGN_UP_URL;
    if (paPoSignUpUrl) window.open(paPoSignUpUrl, "_blank");
  };

  const [getSessionStorageItem] = useSessionStorage<Tariff[]>(
    SESSION_KEYS.TARIFFS,
  );

  const [getBonus] = useSessionStorage<BONUS | null>(SESSION_KEYS.BONUS);
  const showBonusCampaignFooter = getBonus()?.isActive ?? false;

  const bonusEndDate = getBonus()?.endDate ?? "";

  const footnoteBonus = t("page.tariff.footnotes.footnote4", {
    bonusEndDate: dateFormat(bonusEndDate),
  });

  const { tariffsData } = useMemo(() => {
    const tariffsData = getSessionStorageItem();
    return { tariffsData };
  }, [getSessionStorageItem]);

  const [showSpinner, setShowSpinner] = useState(true);

  const hasTariffs = Array.isArray(tariffsData) && tariffsData.length > 0;

  const [, setTariffs] = useSessionStorage<Tariff[]>(SESSION_KEYS.TARIFFS);
  const [, setBonus] = useSessionStorage<BONUS | null>(SESSION_KEYS.BONUS);

  const openSummary = searchParams.get("openSummary") === "true";

  const videoRef = useRef<HTMLVideoElement>(null);

  const [, setShowVideo] = useState({
    width: "0px",
    height: "0px",
  });

  document.addEventListener("fullscreenchange", () => {
    if (document.fullscreenElement === null) {
      videoRef.current?.pause();
      setShowVideo({
        width: "0px",
        height: "0px",
      });
    }
  });

  if (videoRef.current) {
    videoRef.current.onpause = function () {
      if (videoRef.current?.seeking) {
        return;
      }
      videoRef.current?.pause();
      setShowVideo({
        width: "0px",
        height: "0px",
      });
    };
  }

  const handleVideoClick = () => {
    setShowVideo({
      width: "100%",
      height: "100%",
    });
    const video: any = document.getElementsByTagName("video")[0];

    if (video.requestFullscreen) {
      video.requestFullscreen();
    } else if (video.msRequestFullscreen) {
      video.msRequestFullscreen();
    } else if (video.mozRequestFullScreen) {
      video.mozRequestFullScreen();
    } else if (video.webkitRequestFullscreen) {
      video.webkitRequestFullscreen();
    }

    video.play();
  };

  useEffect(() => {
    if (!openSummary && tid === "") {
      setShowSpinner(false);
      return;
    }

    let latestTariffs: Tariff[] = [];

    try {
      const initialData = sessionStorage.getItem(SESSION_KEYS.TARIFFS);
      if (initialData) {
        latestTariffs = JSON.parse(initialData);
        setTariffs(latestTariffs);
      }
    } catch {}

    if (openSummary) {
      setShowSpinner(false);
      return;
    }

    const interval = setInterval(() => {
      try {
        const storage = sessionStorage.getItem(SESSION_KEYS.TARIFFS);
        if (!storage) return;

        const parsed = JSON.parse(storage) as Tariff[];

        if (JSON.stringify(parsed) !== JSON.stringify(latestTariffs)) {
          latestTariffs = parsed;
          setTariffs(parsed);
        }
      } catch {}
    }, 500);

    const timer = setTimeout(() => {
      if (latestTariffs.length > 0) {
        setShowSpinner(false);
      } else {
        setShowSpinner(false);
      }

      clearInterval(interval);
    }, 5000);

    return () => {
      clearInterval(interval);
      clearTimeout(timer);
    };
  }, [openSummary, tid, navigate, setTariffs]);

  return (
    <Stack width="100%" alignItems="center">
      <Stack
        sx={{
          width: "100%",
          maxWidth: "1100px",
          px: { xs: 2, sm: 3 },
          mt: -8,
          mb: 8,
          alignItems: "center",
        }}
      >
        <Text
          sx={{
            color: "black",
            fontSize: { xs: "22px", md: "28px" },
            fontWeight: "700",
            lineHeight: "34px",
            fontFamily: "isonType",
            letterSpacing: "-0.4px",
            textAlign: "center",
            my: 8,
          }}
          text={t("page.calculatedSavingsSummary.titleLichtblick")}
        />

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 3, md: 4 }}
          width="100%"
          justifyContent="center"
          alignItems={{ xs: "center", md: "flex-start" }}
        >
          <Stack
            sx={{
              width: "100%",
              maxWidth: { xs: "100%", md: "400px" },
              flex: "1 1 auto",
            }}
            gap={3}
            alignItems="center"
          >
            {showSpinner ? (
              <Box sx={{ width: "100%", overflow: "hidden" }}>
                <PreCalcSkeletonSummary />
              </Box>
            ) : (
              hasTariffs && (
                <Box sx={{ width: "100%", overflow: "hidden" }}>
                  <CalculatedSavingsSummary
                    tariffsData={tariffsData}
                    showSavingsResult={true}
                    getLogo={() => ({ houseImg: "/path/to/img", cssProps: {} })}
                  />
                </Box>
              )
            )}

            <Stack gap={2} width="100%" maxWidth={{ xs: "100%", sm: "360px" }}>
              {hasTariffs && (
                <PrimaryButton
                  onClick={() => navigate(ROUTES.TARIF)}
                  sx={{
                    width: "100%",
                    minHeight: "48px",
                    borderRadius: "24px",
                    fontSize: "16px",
                    textTransform: "none",
                  }}
                  text={t("page.calculatedSavingsSummary.tarifDetails")}
                />
              )}

              <PrimaryButton
                onClick={() => {
                  setBonus(null);
                  setTariffs([]);
                  handleStartCalculator();
                }}
                sx={{
                  width: "100%",
                  minHeight: "48px",
                  borderRadius: "24px",
                  fontSize: hasTariffs ? "13px" : "15px",
                  fontWeight: hasTariffs ? 400 : undefined,
                  textTransform: "none",
                  border: "none",
                  bgcolor: hasTariffs ? "transparent" : undefined,
                  color: hasTariffs ? "#000000" : undefined,
                  "&:hover": {
                    backgroundColor: hasTariffs
                      ? "rgba(0, 0, 0, 0.04)"
                      : undefined,
                  },
                }}
                text={
                  hasTariffs
                    ? t("page.introduction.textNewCalculation")
                    : t("page.introduction.textCalculation")
                }
              />
            </Stack>
          </Stack>

          <Stack
            sx={{
              width: "100%",
              maxWidth: { xs: "100%", md: "400px" },
              flex: "1 1 auto",
            }}
            gap={3}
            alignItems="center"
          >
            <Box
              sx={{
                width: "100%",
                bgcolor: "#FFFFFF",
                borderRadius: "16px",
                p: { xs: 2.5, sm: 3 },
                boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.04)",
                border: "1px solid #E5E7EB",
                display: "flex",
                flexDirection: "column",
                gap: 3,
                boxSizing: "border-box",
              }}
            >
              <Stack
                direction="row"
                spacing={6}
                alignItems="center"
                sx={{
                  mx: { md: "30px" },
                }}
              >
                <Box
                  component="img"
                  src={TestemonialPortrait}
                  alt="Max M."
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    objectFit: "cover",
                    flexShrink: 0,
                  }}
                />
                <Stack gap={1}>
                  <Text
                    text="„Endlich spare ich mit meiner PV-Anlage und meinem E-Auto noch mehr Stromkosten, automatisch.“"
                    sx={{
                      fontSize: "14px",
                      fontWeight: "400",
                      lineHeight: "20px",
                      color: "#333333",
                    }}
                  />
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    gap={1}
                  >
                    <Text
                      text="Max M., Hamburg"
                      sx={{
                        fontSize: "12px",
                        color: "#31825E",
                        fontWeight: "600",
                      }}
                    />
                    <Box
                      sx={{
                        bgcolor: "#C6FFB2",
                        color: "#31825E",
                        fontSize: "11px",
                        fontWeight: "700",
                        px: 1,
                        py: 0.25,
                        borderRadius: "12px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      spart 340 € / Jahr
                    </Box>
                  </Stack>
                </Stack>
              </Stack>

              <Divider />

              <Stack gap={1.5} alignItems="center">
                <CertificateSection smallerBadges={true} />
              </Stack>
            </Box>
          </Stack>
        </Stack>
      </Stack>

      <Box
        sx={(theme) => ({
          width: {
            xs: "calc(100% + 25px)",
            md: "calc(100% + 200px)",
          },
          bgcolor: "#F4F4F4",
          py: { xs: 4, md: 8 },
          px: { xs: 2, sm: 4, md: 6 },
          display: "flex",
          justifyContent: "center",
          boxSizing: "border-box",
        })}
      >
        <Stack
          sx={{
            width: "100%",
            maxWidth: "1100px",
            alignItems: "center",
            gap: { xs: 2.5, md: 10 },
            marginTop: 4,
          }}
        >
          <BoldSubtitle
            text={t("page.introduction.textTitle")}
            sx={{
              textAlign: "center",
              fontSize: { xs: "18px", sm: "22px", md: "28px" },
              lineHeight: { xs: "24px", md: "34px" },
            }}
          />

          <Stack
            direction={{ xs: "column", md: "row" }}
            gap={3}
            width="100%"
            justifyContent="center"
            alignItems={{ xs: "stretch", md: "flex-start" }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: { xs: "100%", md: "620px" },
                bgcolor: "#FFFFFF",
                borderRadius: "16px",
                p: { xs: 2, sm: 3.5 },
                boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.04)",
                border: "1px solid #E5E7EB",
                boxSizing: "border-box",
              }}
            >
              <Stack gap={1}>
                <Accordion
                  disableGutters
                  elevation={0}
                  square
                  expanded={expandedPanel === "panel1"}
                  onChange={handleAccordionChange("panel1")}
                  sx={{
                    "&:before": { display: "none" },
                    bgcolor: "transparent",
                    borderBottom: "1px solid #F0F0F0",
                    pb: 1,
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      px: 0,
                      "& .MuiAccordionSummary-content": { margin: 0 },
                    }}
                  >
                    <Stack direction="row" gap={1.5} alignItems="center">
                      <CheckCircle />
                      <TextSubtitle
                        sx={{
                          fontWeight: "500",
                          fontSize: { xs: "14px", sm: "16px", md: "17px" },
                          lineHeight: { xs: "20px", md: "22px" },
                          py: 6,
                        }}
                        text={t(
                          "page.introductionSMA.textSubtitleOnePoint1Title",
                        )}
                      />
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{ px: 0, pl: { xs: 3.5, sm: 5 }, pt: 0.5 }}
                  >
                    <TextSubtitle
                      sx={{
                        fontSize: { xs: "13px", sm: "14px", md: "15px" },
                        lineHeight: { xs: "19px", md: "22px" },
                        color: "#555555",
                      }}
                      text={t("page.introductionSMA.textSubtitleOnePoint1Text")}
                    />
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  disableGutters
                  elevation={0}
                  square
                  expanded={expandedPanel === "panel2"}
                  onChange={handleAccordionChange("panel2")}
                  sx={{
                    "&:before": { display: "none" },
                    bgcolor: "transparent",
                    borderBottom: "1px solid #F0F0F0",
                    pb: 1,
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      px: 0,
                      "& .MuiAccordionSummary-content": { margin: 0 },
                    }}
                  >
                    <Stack direction="row" gap={1.5} alignItems="center">
                      <CheckCircle />
                      <TextSubtitle
                        sx={{
                          fontWeight: "500",
                          fontSize: { xs: "14px", sm: "16px", md: "17px" },
                          lineHeight: { xs: "20px", md: "22px" },
                          py: 6,
                        }}
                        text={t(
                          "page.introductionSMA.textSubtitleOnePoint2LichtblickTitle",
                        )}
                      />
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{ px: 0, pl: { xs: 3.5, sm: 5 }, pt: 0.5 }}
                  >
                    <TextSubtitle
                      sx={{
                        fontSize: { xs: "13px", sm: "14px", md: "15px" },
                        lineHeight: { xs: "19px", md: "22px" },
                        color: "#555555",
                      }}
                      text={t(
                        "page.introductionSMA.textSubtitleOnePoint2LichtblickText",
                      )}
                    />
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  disableGutters
                  elevation={0}
                  square
                  expanded={expandedPanel === "panel3"}
                  onChange={handleAccordionChange("panel3")}
                  sx={{
                    "&:before": { display: "none" },
                    bgcolor: "transparent",
                    borderBottom: "1px solid #F0F0F0",
                    pb: 1,
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      px: 0,
                      "& .MuiAccordionSummary-content": { margin: 0 },
                    }}
                  >
                    <Stack direction="row" gap={1.5} alignItems="center">
                      <CheckCircle />
                      <TextSubtitle
                        sx={{
                          fontWeight: "500",
                          fontSize: { xs: "14px", sm: "16px", md: "17px" },
                          lineHeight: { xs: "20px", md: "22px" },
                          py: 6,
                        }}
                        text={t(
                          "page.introductionSMA.textSubtitleOnePoint3Title",
                        )}
                      />
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{ px: 0, pl: { xs: 3.5, sm: 5 }, pt: 0.5 }}
                  >
                    <TextSubtitle
                      sx={{
                        fontSize: { xs: "13px", sm: "14px", md: "15px" },
                        lineHeight: { xs: "19px", md: "22px" },
                        color: "#555555",
                      }}
                      text={t("page.introductionSMA.textSubtitleOnePoint3Text")}
                    />
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  disableGutters
                  elevation={0}
                  square
                  expanded={expandedPanel === "panel4"}
                  onChange={handleAccordionChange("panel4")}
                  sx={{
                    "&:before": { display: "none" },
                    bgcolor: "transparent",
                    borderBottom: "1px solid #F0F0F0",
                    pb: 1,
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      px: 0,
                      "& .MuiAccordionSummary-content": { margin: 0 },
                    }}
                  >
                    <Stack direction="row" gap={1.5} alignItems="center">
                      <CheckCircle />
                      <TextSubtitle
                        sx={{
                          fontWeight: "500",
                          fontSize: { xs: "14px", sm: "16px", md: "17px" },
                          lineHeight: { xs: "20px", md: "22px" },
                          py: 6,
                        }}
                        text={t(
                          "page.introductionSMA.textSubtitleOnePoint4LichtblickTitle",
                        )}
                      />
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{ px: 0, pl: { xs: 3.5, sm: 5 }, pt: 0.5 }}
                  >
                    <TextSubtitle
                      sx={{
                        fontSize: { xs: "13px", sm: "14px", md: "15px" },
                        lineHeight: { xs: "19px", md: "22px" },
                        color: "#555555",
                      }}
                      text={t(
                        "page.introductionSMA.textSubtitleOnePoint4LichtblickText",
                      )}
                    />
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  disableGutters
                  elevation={0}
                  square
                  expanded={expandedPanel === "panel5"}
                  onChange={handleAccordionChange("panel5")}
                  sx={{
                    "&:before": { display: "none" },
                    bgcolor: "transparent",
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      px: 0,
                      "& .MuiAccordionSummary-content": { margin: 0 },
                    }}
                  >
                    <Stack direction="row" gap={1.5} alignItems="center">
                      <CheckCircle />
                      <TextSubtitle
                        sx={{
                          fontWeight: "500",
                          fontSize: { xs: "14px", sm: "16px", md: "17px" },
                          lineHeight: { xs: "20px", md: "22px" },
                          py: 6,
                        }}
                        text={t(
                          "page.introductionSMA.textSubtitleOnePoint5Title",
                        )}
                      />
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{ px: 0, pl: { xs: 3.5, sm: 5 }, pt: 0.5 }}
                  >
                    <TextSubtitle
                      sx={{
                        fontSize: { xs: "13px", sm: "14px", md: "15px" },
                        lineHeight: { xs: "19px", md: "22px" },
                        color: "#555555",
                      }}
                      text={t("page.introductionSMA.textSubtitleOnePoint5Text")}
                    />
                  </AccordionDetails>
                </Accordion>
              </Stack>
            </Box>

            <Box
              sx={{
                width: "100%",
                maxWidth: { xs: "100%", md: "440px" },
                bgcolor: "#FFFFFF",
                borderRadius: "16px",
                p: { xs: 2.5, sm: 3.5 },
                boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.04)",
                border: "1px solid #E5E7EB",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2.5,
                boxSizing: "border-box",
              }}
            >
              <Box
                component="img"
                src={StromPreiseSMA}
                alt="Strompreise SMA"
                sx={{
                  width: "100%",
                  height: "auto",
                  maxHeight: "220px",
                  objectFit: "contain",
                }}
              />

              <Stack gap={4} width="100%">
                {hasTariffs && (
                  <PrimaryButton
                    onClick={() => navigate(ROUTES.TARIF)}
                    text={t("page.calculatedSavingsSummary.tarifDetails")}
                    sx={{
                      width: "100%",
                      minHeight: "48px",
                      borderRadius: "24px",
                      fontSize: { xs: "14px", sm: "16px" },
                      textTransform: "none",
                    }}
                  />
                )}

                <PrimaryButton
                  onClick={handleVideoClick}
                  text={
                    <Stack direction="row" alignItems="center" gap={1.5}>
                      <PlayCircleBlack />
                      {t("page.introductionSMA.titleVideo")}
                    </Stack>
                  }
                  sx={{
                    width: "100%",
                    minHeight: "48px",
                    borderRadius: "24px",
                    fontSize: { xs: "14px", sm: "15px" },
                    color: "#000000",
                    bgcolor: "transparent",
                    border: "1.5px solid #000000",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                />
              </Stack>
            </Box>
          </Stack>
        </Stack>
      </Box>

      <Stack
        gap={4}
        sx={{
          width: "100%",
          maxWidth: "880px",
          px: { xs: 2, sm: 3 },
          mt: 6,
        }}
      >
        <Text
          text={t("page.introductionSMA.footNote")}
          sx={{ lineHeight: "21px" }}
        />
        <Text
          text={t("page.introduction.footNote2")}
          sx={{ lineHeight: "21px" }}
        />
        <Text
          text={t("page.introductionSMA.footNote3")}
          sx={{ lineHeight: "21px" }}
        />
        <Box
          sx={(theme) => ({
            lineHeight: "21px",
            color: theme.palette.app.secondaryFont,
            "& a": {
              color: "inherit",
              textDecoration: "none",
            },
            "& a:hover": {
              textDecoration: "underline",
            },
          })}
        >
          <Trans
            i18nKey="page.introductionSMA.footNote4"
            components={{
              1: (
                <a
                  href="https://www.lichtblick.de/oekostrom/stromkennzeichnung/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="View electricity labeling"
                />
              ),
            }}
          />
        </Box>

        {showBonusCampaignFooter && (
          <Text sx={{ lineHeight: "21px" }} text={footnoteBonus} />
        )}

        <Divider />
      </Stack>

      <StartCalculatorModal
        isOpen={isModalOpen}
        onClose={toggleModal}
        onSubmit={handleModalSubmit}
        title={t("page.introductionSMA.modal.title")}
        subtitle1={t("page.introductionSMA.modal.subtitle1")}
        subtitle2={t("page.introductionSMA.modal.subtitle2")}
        subtitle3={t("page.introductionSMA.modal.subtitle3")}
        submitButtonText={t("page.introductionSMA.modal.submitButtonText")}
      />
    </Stack>
  );
};

export default Content;
